// === Account Dialog ===
// Shows the logged-in user's profile details and allows password reset.

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth, getSupabaseClient } from '../lib/auth';
import { MfaSection } from './MfaSection';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserProfile {
  readonly id: string;
  readonly email: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly username: string;
  readonly phone_number: string;
  readonly mobile_number: string;
  readonly role: string;
  readonly last_sign_in_at: string | null;
  readonly created_at: string | null;
}

interface AccountDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: true,
  }).format(new Date(iso));
}

function roleLabel(role: string): string {
  const map: Record<string, string> = {
    superadmin: 'Superadmin',
    companies_admin: 'Company Admin',
    companies_staff: 'Company Staff',
    companies_agronomist: 'Agronomist',
    companies_farm_manager: 'Farm Manager',
    companies_spray_operator: 'Spray Operator',
    companies_farm_hand: 'Farm Hand',
    labour: 'Labour',
  };
  return map[role] ?? role;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AccountDialog({ open, onClose }: AccountDialogProps) {
  const { user, session } = useAuth();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Editable profile form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null);
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);

  // Password reset state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ---------------------------------------------------------------------------
  // Fetch profile
  // ---------------------------------------------------------------------------

  const fetchProfile = useCallback(async () => {
    if (!user || !session) return;
    setLoadingProfile(true);
    setProfileError(null);
    try {
      const apiBase = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787';
      const res = await fetch(`${apiBase}/api/v1/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error(`Failed to load profile (${res.status})`);
      const json = (await res.json()) as { data: UserProfile };
      setProfile(json.data);
      setFirstName(json.data.first_name);
      setLastName(json.data.last_name);
      setUsername(json.data.username);
      setPhone(json.data.phone_number);
      setMobile(json.data.mobile_number);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoadingProfile(false);
    }
  }, [user, session]);

  useEffect(() => {
    if (open) {
      void fetchProfile();
      setNewPassword('');
      setConfirmPassword('');
      setSaveError(null);
      setSaveSuccess(false);
      setProfileSaveError(null);
      setProfileSaveSuccess(false);
    }
  }, [open, fetchProfile]);

  // ---------------------------------------------------------------------------
  // Save profile
  // ---------------------------------------------------------------------------

  const handleSaveProfile = async () => {
    if (!user || !session || !profile) return;
    setProfileSaving(true);
    setProfileSaveError(null);
    setProfileSaveSuccess(false);
    try {
      const apiBase = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787';
      const res = await fetch(`${apiBase}/api/v1/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: profile.email,
          role: profile.role,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          username: username.trim(),
          phone_number: phone.trim(),
          mobile_number: mobile.trim(),
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `Failed to save (${res.status})`);
      }
      const json = (await res.json()) as { data: UserProfile };
      setProfile(json.data);
      setProfileSaveSuccess(true);
    } catch (err) {
      setProfileSaveError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Password reset
  // ---------------------------------------------------------------------------

  const handlePasswordReset = async () => {
    if (!newPassword) { setSaveError('Please enter a new password.'); return; }
    if (newPassword.length < 8) { setSaveError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setSaveError('Passwords do not match.'); return; }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw new Error(error.message);
      setSaveSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const displayProfile = profile ?? {
    id: user?.id ?? '',
    email: user?.email ?? '',
    first_name: '',
    last_name: '',
    username: '',
    phone_number: '',
    mobile_number: '',
    role: (user?.app_metadata?.['role'] as string) ?? '',
    last_sign_in_at: user?.last_sign_in_at ?? null,
    created_at: user?.created_at ?? null,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" fontSize="small" />
          My Account
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {loadingProfile ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : profileError ? (
          <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>
        ) : null}

        {/* Profile details */}
        <Box mt={1} p={2} sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08), borderRadius: 1 }}>
          <Typography variant="caption" fontWeight={700} color="primary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1.5 }}>
            Profile
          </Typography>

          {/* Read-only: email, role, dates */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">Email</Typography>
              <Typography variant="body2" fontWeight={600}>{displayProfile.email || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">Role</Typography>
              <Chip
                label={roleLabel(displayProfile.role)}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: 11, height: 20 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">Last Sign In</Typography>
              <Typography variant="body2">{fmtDate(displayProfile.last_sign_in_at)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">Account Created</Typography>
              <Typography variant="body2">{fmtDate(displayProfile.created_at)}</Typography>
            </Box>
          </Box>

          {/* Editable fields */}
          {profileSaveSuccess && (
            <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setProfileSaveSuccess(false)}>
              Profile updated successfully.
            </Alert>
          )}
          {profileSaveError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setProfileSaveError(null)}>
              {profileSaveError}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField
              label="First Name"
              size="small"
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loadingProfile}
            />
            <TextField
              label="Last Name"
              size="small"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loadingProfile}
            />
            <TextField
              label="Username"
              size="small"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loadingProfile}
            />
            <Box />
            <TextField
              label="Phone"
              size="small"
              fullWidth
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={loadingProfile}
            />
            <TextField
              label="Mobile"
              size="small"
              fullWidth
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              disabled={loadingProfile}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              disabled={profileSaving || loadingProfile}
              onClick={() => void handleSaveProfile()}
              startIcon={profileSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
            >
              {profileSaving ? 'Saving…' : 'Save Profile'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Auth Info */}
        <Box p={2} sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.07), borderRadius: 1, border: '1px solid', borderColor: (t) => alpha(t.palette.success.main, 0.2) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <ShieldIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="caption" fontWeight={700} color="success.main"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Auth Info
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="text.disabled" display="block">User ID</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' }}>
                {user?.id ?? '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">Email Confirmed</Typography>
              <Chip
                label={user?.email_confirmed_at ? 'Confirmed' : 'Not confirmed'}
                size="small"
                color={user?.email_confirmed_at ? 'success' : 'warning'}
                variant="outlined"
                sx={{ fontSize: 11, height: 20 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">Auth Provider</Typography>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {(user?.app_metadata?.['provider'] as string) ?? 'email'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">MFA Factors</Typography>
              <Typography variant="body2">
                {(user?.factors?.length ?? 0) > 0
                  ? user!.factors!.map((f) => f.factor_type).join(', ')
                  : 'None'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">Email Confirmed At</Typography>
              <Typography variant="body2">{fmtDate(user?.email_confirmed_at ?? null)}</Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Two-factor — self-enrolment only; see MfaSection for why */}
        <MfaSection />

        <Divider sx={{ my: 2 }} />

        {/* Password reset */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <LockIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>Reset Password</Typography>
          </Box>

          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSaveSuccess(false)}>
              Password updated successfully.
            </Alert>
          )}
          {saveError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              label="New Password"
              size="small"
              fullWidth
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowNew((v) => !v)} edge="end">
                      {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              size="small"
              fullWidth
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm((v) => !v)} edge="end">
                      {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} size="small">Close</Button>
        <Button
          variant="contained"
          size="small"
          disabled={saving || !newPassword || !confirmPassword}
          onClick={() => void handlePasswordReset()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <LockIcon fontSize="small" />}
        >
          {saving ? 'Updating…' : 'Update Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
